import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import RecipeCardDialog from "@/components/RecipeCardDialog";
import { Plus, Trash2, Upload, Link2, FileText, Copy, Pencil, TrendingUp, Users as UsersIcon, IndianRupee, Send, Sparkles, MessageSquareWarning, Lightbulb, Eye, History, ChefHat, MessageSquare, Activity, ClipboardList, Utensils } from "lucide-react";

export default function Admin() {
  const [tab, setTab] = useState("analytics");

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="text-overline">admin panel</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight mb-8">Operator console</h1>
        <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
          <TabsList className="bg-surface rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="analytics" className="rounded-full px-6" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="ask" className="rounded-full px-6" data-testid="tab-ask">Ask</TabsTrigger>
            <TabsTrigger value="agents" className="rounded-full px-6" data-testid="tab-agents">Agents</TabsTrigger>
            <TabsTrigger value="users" className="rounded-full px-6" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="invites" className="rounded-full px-6" data-testid="tab-invites">Invites</TabsTrigger>
            <TabsTrigger value="support" className="rounded-full px-6" data-testid="tab-support">Support</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-full px-6" data-testid="tab-payments">Payments</TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
          <TabsContent value="ask" className="mt-6"><AskTab /></TabsContent>
          <TabsContent value="agents" className="mt-6"><AgentsTab /></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="invites" className="mt-6"><InvitesTab /></TabsContent>
          <TabsContent value="support" className="mt-6"><SupportTab /></TabsContent>
          <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function AnalyticsTab() {
  const [d, setD] = useState(null);
  const [ins, setIns] = useState(null);
  const [insLoading, setInsLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/analytics/overview");
    setD(data);
  };
  const loadInsights = async (refresh = false) => {
    setInsLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/conversations${refresh ? "?refresh=true" : ""}`);
      setIns(data);
    } catch (e) { toast.error("Couldn't load insights"); }
    finally { setInsLoading(false); }
  };
  useEffect(() => { load(); loadInsights(); }, []);

  if (!d) return <div className="text-muted-foreground">Loading metrics…</div>;
  const tc = d.tier_counts || {};
  const maxAct = Math.max(1, ...(d.activity_14d || []).map((a) => a.count));

  const Card = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="bg-surface-elevated border border-border rounded-3xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-overline text-secondary"><Icon size={14} /> {label}</div>
      <div className="font-serif-display text-4xl text-primary mt-2">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={UsersIcon} label="Total sign-ups" value={d.total_users} sub={`+${d.signups_7d} this week · +${d.signups_30d} this month`} testid="stat-signups" />
        <Card icon={TrendingUp} label="Active users" value={d.active_7d} sub={`7-day · ${d.active_30d} in 30 days`} testid="stat-active" />
        <Card icon={IndianRupee} label="MRR" value={`₹${(d.mrr_inr || 0).toLocaleString("en-IN")}`} sub={`ARR ₹${(d.arr_inr || 0).toLocaleString("en-IN")}`} testid="stat-mrr" />
        <Card icon={Sparkles} label="Paid users" value={(tc.pro || 0) + (tc.clinic || 0)} sub={`Free ${tc.free || 0} · Pro ${tc.pro || 0} · Clinic ${tc.clinic || 0}`} testid="stat-paid" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-surface-elevated border border-border rounded-3xl p-5 lg:col-span-2" data-testid="activity-chart">
          <div className="text-overline mb-3">activity — last 14 days</div>
          <div className="flex items-end gap-1.5 h-32">
            {(d.activity_14d || []).map((a) => (
              <div key={a.date} className="flex-1 flex flex-col items-center gap-1" title={`${a.date}: ${a.count}`}>
                <div className="w-full bg-secondary/70 rounded-t" style={{ height: `${(a.count / maxAct) * 100}%`, minHeight: a.count ? 4 : 1 }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{(d.activity_14d || [])[0]?.date?.slice(5)}</span>
            <span>{(d.activity_14d || [])[d.activity_14d.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
        <div className="bg-surface-elevated border border-border rounded-3xl p-5" data-testid="totals-card">
          <div className="text-overline mb-3">lifetime totals</div>
          <Row k="Chat messages" v={(d.totals?.chats || 0).toLocaleString()} />
          <Row k="Meals logged" v={(d.totals?.food_logs || 0).toLocaleString()} />
          <Row k="Meal plans" v={(d.totals?.meal_plans || 0).toLocaleString()} />
          <Row k="Plan mix" v={`M${d.plan_counts?.monthly || 0}/A${d.plan_counts?.annual || 0}/L${d.plan_counts?.lifetime || 0}`} />
        </div>
      </div>

      {/* Conversation insights */}
      <div className="bg-surface-elevated border border-border rounded-3xl p-5" data-testid="insights-card">
        <div className="flex items-center justify-between mb-2">
          <div className="text-overline">what users are talking about {ins?.sample_size ? `(${ins.sample_size} msgs)` : ""}</div>
          <Button size="sm" variant="outline" onClick={() => loadInsights(true)} disabled={insLoading} className="rounded-full" data-testid="refresh-insights-btn">{insLoading ? "Analyzing…" : "Refresh"}</Button>
        </div>
        {!ins ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          <>
            <p className="text-sm text-foreground/80 mb-4">{ins.summary}</p>
            <div className="grid md:grid-cols-3 gap-4">
              <InsightList icon={TrendingUp} title="Themes" items={ins.themes} tone="bg-secondary/15 text-secondary" />
              <InsightList icon={MessageSquareWarning} title="Complaints" items={ins.complaints} tone="bg-destructive/10 text-destructive" />
              <InsightList icon={Lightbulb} title="Feature requests" items={ins.feature_requests} tone="bg-accent/20 text-accent-foreground" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 py-2">
      <span className="text-sm text-muted-foreground">{k}</span>
      <span className="font-medium text-primary">{v}</span>
    </div>
  );
}

function InsightList({ icon: Icon, title, items, tone }) {
  return (
    <div>
      <div className="text-overline flex items-center gap-1 mb-2"><Icon size={12} /> {title}</div>
      <div className="space-y-1.5">
        {(items || []).map((x, i) => <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg ${tone}`}>{x}</div>)}
        {(!items || items.length === 0) && <div className="text-xs text-muted-foreground">None detected</div>}
      </div>
    </div>
  );
}

function AskTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const SUGGESTIONS = [
    "How many sign-ups and what's our MRR?",
    "What are users complaining about most?",
    "Summarize feature requests we should prioritize",
    "How engaged are users this week?",
  ];

  const load = async () => {
    const { data } = await api.get("/admin/chat/messages");
    setMessages(data.messages || []);
  };
  useEffect(() => { load(); }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    setSending(true);
    try {
      const { data } = await api.post("/admin/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, id: `a-${Date.now()}` }]);
    } catch (e) {
      toast.error("Copilot failed — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-surface-elevated border border-border rounded-3xl p-5 flex flex-col h-[68vh]" data-testid="ask-tab">
      <div className="text-overline mb-1 flex items-center gap-1"><Sparkles size={12} /> operator copilot</div>
      <p className="text-xs text-muted-foreground mb-3">Ask anything about your platform data — signups, revenue, engagement, and what users are saying. Answers use live metrics + conversation insights.</p>
      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-3 pr-1" data-testid="ask-messages">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-surface border border-border hover:bg-muted text-foreground/70" data-testid="ask-suggestion">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${m.role}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-surface border border-border"}`}>
              {m.role === "assistant" ? <Markdown>{m.content}</Markdown> : m.content}
            </div>
          </div>
        ))}
        {sending && <div className="text-xs text-muted-foreground">Copilot is thinking…</div>}
      </div>
      <div className="flex items-end gap-2 mt-3 pt-3 border-t border-border">
        <Textarea
          rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about signups, revenue, engagement, complaints…"
          className="rounded-2xl bg-surface resize-none" data-testid="ask-input"
        />
        <Button onClick={() => send()} disabled={sending || !input.trim()} className="rounded-full bg-primary h-11 w-11 p-0" data-testid="ask-send"><Send size={16} /></Button>
      </div>
    </div>
  );
}

function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", description: "", characteristics: "", enabled: true, admin_only: false });
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/agents");
    setAgents(data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => setForm({ name: "", role: "", description: "", characteristics: "", enabled: true, admin_only: false });
  const submit = async () => {
    if (!form.name.trim() || !form.role.trim()) return toast.warning("Name and role required");
    if (editing) {
      await api.patch(`/admin/agents/${editing.id}`, form);
      toast.success("Agent updated");
    } else {
      await api.post("/admin/agents", form);
      toast.success("Agent created");
    }
    setOpen(false); setEditing(null); reset(); load();
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, role: a.role, description: a.description || "", characteristics: a.characteristics || "", enabled: !!a.enabled, admin_only: !!a.admin_only });
    setOpen(true);
  };
  const toggle = async (a) => {
    await api.patch(`/admin/agents/${a.id}`, { enabled: !a.enabled });
    load();
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete agent "${a.name}"?`)) return;
    await api.delete(`/admin/agents/${a.id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); reset(); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-agent-btn" className="rounded-full bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" /> New agent</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit agent" : "Create agent"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Keto Coach" /></div>
              <div><Label>Role / skill</Label><Input data-testid="agent-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="diet | calorie | recipe | inventory | chef" /></div>
              <div><Label>Description</Label><Input data-testid="agent-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Behavior / characteristics</Label>
                <Textarea data-testid="agent-char" rows={5} value={form.characteristics} onChange={(e) => setForm({ ...form, characteristics: e.target.value })} placeholder="How should this agent respond? Tone, format, constraints…" />
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="agent-enabled" /> Enabled</div>
              <div className="flex items-center gap-2"><Switch checked={form.admin_only} onCheckedChange={(v) => setForm({ ...form, admin_only: v })} data-testid="agent-admin-only" /> <span>Admin-only <span className="text-xs text-muted-foreground">(exclusive to admin — never runs in user chat)</span></span></div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="create-agent-btn" className="rounded-full bg-primary hover:bg-primary/90">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {agents.map((a) => (
          <div key={a.id} className="bg-surface-elevated rounded-3xl border border-border p-5" data-testid={`agent-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-overline text-secondary">{a.role || "specialist"}</div>
                <div className="font-serif-display text-2xl text-primary flex items-center gap-2">
                  {a.name}
                  {a.admin_only && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20" data-testid={`admin-only-badge-${a.id}`}>Admin-only</span>}
                </div>
                <div className="text-sm text-foreground/70 mt-1">{a.description}</div>
                {a.characteristics && <div className="text-xs text-muted-foreground mt-2 line-clamp-2 italic">"{a.characteristics}"</div>}
              </div>
              <div className="flex items-center gap-1">
                <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`toggle-${a.id}`} />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">{(a.sources || []).length} knowledge source{(a.sources || []).length === 1 ? "" : "s"} • {(a.knowledge_text || "").length.toLocaleString()} chars</div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(a)} className="rounded-full" data-testid={`edit-agent-${a.id}`}><Pencil size={12} className="mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => setSelected(a)} className="rounded-full" data-testid={`manage-${a.id}`}>Knowledge</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(a)} className="rounded-full text-destructive hover:text-destructive" data-testid={`del-agent-${a.id}`}><Trash2 size={12} className="mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
        {agents.length === 0 && <div className="text-muted-foreground">No agents yet.</div>}
      </div>

      <KnowledgeDialog agent={selected} onClose={() => { setSelected(null); load(); }} />
    </div>
  );
}

function KnowledgeDialog({ agent, onClose }) {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);

  if (!agent) return null;

  const uploadPdf = async (file) => {
    if (file.size > 20 * 1024 * 1024) return toast.error("PDF exceeds 20MB");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("tuha_token");
      const res = await fetch(`${API}/admin/agents/${agent.id}/upload-pdf`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(`Added (${data.extracted_chars.toLocaleString()} chars)`);
      onClose();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addUrl = async () => {
    if (!url.startsWith("http")) return toast.warning("Enter a valid URL");
    setAdding(true);
    try {
      const { data } = await api.post(`/admin/agents/${agent.id}/add-url`, { url });
      toast.success(`Added URL (${data.extracted_chars.toLocaleString()} chars)`);
      setUrl("");
      onClose();
    } catch (e) {
      toast.error("URL add failed");
    } finally {
      setAdding(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all knowledge sources?")) return;
    await api.post(`/admin/agents/${agent.id}/clear-knowledge`);
    toast.success("Cleared");
    onClose();
  };

  return (
    <Dialog open={!!agent} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-3xl max-w-2xl">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Knowledge — {agent.name}</DialogTitle></DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border-2 border-dashed border-border rounded-2xl p-6 bg-surface text-center">
            <Upload size={22} className="mx-auto text-secondary mb-2" />
            <div className="text-sm font-medium">Upload PDF (max 20MB)</div>
            <input
              type="file"
              accept="application/pdf"
              id="pdf-upload"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadPdf(e.target.files[0])}
              data-testid="pdf-upload-input"
            />
            <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => document.getElementById("pdf-upload").click()} disabled={uploading} data-testid="pdf-upload-btn">
              {uploading ? "Uploading…" : "Choose file"}
            </Button>
          </div>
          <div className="border-2 border-dashed border-border rounded-2xl p-6 bg-surface">
            <Link2 size={22} className="text-secondary mb-2" />
            <div className="text-sm font-medium mb-2">Add URL</div>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="url-input" />
            <Button size="sm" className="mt-3 rounded-full bg-primary" onClick={addUrl} disabled={adding} data-testid="url-add-btn">
              {adding ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>

        <div className="text-overline mt-4">Sources ({(agent.sources || []).length})</div>
        <div className="max-h-48 overflow-y-auto scrollbar-thin space-y-1">
          {(agent.sources || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1.5 border-b border-border">
              <FileText size={14} className="text-muted-foreground" />
              <span className="truncate flex-1">{s.name}</span>
              <span className="text-xs text-muted-foreground">{(s.chars || 0).toLocaleString()} chars</span>
            </div>
          ))}
          {(agent.sources || []).length === 0 && <div className="text-muted-foreground text-sm py-2">No sources yet.</div>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={clearAll} className="rounded-full" data-testid="clear-knowledge-btn">Clear all</Button>
          <Button onClick={onClose} className="rounded-full bg-primary">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvitesTab() {
  const [invites, setInvites] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState(0);
  const [editingCode, setEditingCode] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/invites");
    setInvites(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    await api.post("/admin/invites", { name, note, max_uses: Number(maxUses) || 0 });
    setName(""); setNote(""); setMaxUses(0);
    load();
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete invite ${code}?`)) return;
    await api.delete(`/admin/invites/${code}`);
    load();
  };
  const copyLink = (code) => {
    const link = `${window.location.origin}/signup/${code}`;
    try {
      navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch (_) {
      toast.info(link);
    }
  };
  const startEdit = (inv) => { setEditingCode(inv.code); setEditName(inv.name || ""); };
  const saveEdit = async (code) => {
    await api.patch(`/admin/invites/${code}`, { name: editName });
    setEditingCode(null); setEditName("");
    load();
    toast.success("Renamed");
  };

  return (
    <div>
      <div className="bg-surface-elevated border border-border rounded-3xl p-5 mb-6">
        <div className="text-overline mb-3">Create invite</div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Input placeholder="Name / label (e.g., Mom)" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="invite-note" />
          <Input type="number" min="0" placeholder="Max uses (0 = unlimited)" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="invite-maxuses" />
          <Button onClick={create} className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-invite-btn">Generate</Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="invites-list">
        {invites.map((i) => (
          <div key={i.code} className="bg-surface-elevated border border-border rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`invite-${i.code}`}>
            <div className="min-w-0 flex-1">
              {editingCode === i.code ? (
                <div className="flex items-center gap-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" data-testid={`edit-name-input-${i.code}`} />
                  <Button size="sm" onClick={() => saveEdit(i.code)} className="rounded-full bg-primary h-8" data-testid={`save-name-${i.code}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingCode(null)} className="rounded-full h-8">Cancel</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-serif-display text-lg text-primary">{i.name || "(unnamed)"}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEdit(i)} data-testid={`edit-name-${i.code}`}><Pencil size={12} /></Button>
                </div>
              )}
              <div className="font-mono text-xs text-muted-foreground mt-0.5">{i.code}</div>
              {i.note && <div className="text-xs text-muted-foreground truncate">{i.note}</div>}
              <div className="text-xs text-muted-foreground">{i.uses || 0}/{i.max_uses ? i.max_uses : "∞"} uses</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyLink(i.code)} className="rounded-full" data-testid={`copy-${i.code}`}>
              <Copy size={14} className="mr-1" /> Copy link
            </Button>
            <Button variant="ghost" size="icon" onClick={() => remove(i.code)} data-testid={`del-invite-${i.code}`}><Trash2 size={16} /></Button>
          </div>
        ))}
        {invites.length === 0 && <div className="text-muted-foreground">No invites yet.</div>}
      </div>
    </div>
  );
}


function planStatusLabel(p) {
  const plan = p?.tier_plan;
  if (!plan) return "free";
  if (plan === "lifetime") return "lifetime";
  const exp = p?.tier_expires_at ? new Date(p.tier_expires_at).getTime() : 0;
  const active = exp > Date.now();
  const names = { trial: "trial", week: "7-day", month: "30-day", year: "12-mo" };
  const nm = names[plan] || plan;
  if (!active) return `${nm} · expired`;
  const days = Math.max(0, Math.ceil((exp - Date.now()) / 86400000));
  return `${nm} · ${days}d left`;
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="bg-surface-elevated border border-border rounded-3xl overflow-hidden" data-testid="users-list">
        <div className="px-5 py-3 text-overline bg-surface flex">
          <div className="flex-1">User</div>
          <div className="w-24 text-right">Daily kcal</div>
          <div className="w-20 text-right">P/C/F</div>
          <div className="w-24 text-right">Status</div>
          <div className="w-28"></div>
        </div>
        {users.map((u) => {
          const p = u.profile || {};
          const mt = p.macro_targets || {};
          return (
            <div key={u.user_id} className="px-5 py-3 flex items-center border-t border-border" data-testid={`user-row-${u.user_id}`}>
              <div className="flex-1 min-w-0 flex items-center gap-3">
                {u.picture && <img src={u.picture} alt="" className="w-9 h-9 rounded-full border border-border" />}
                <div className="min-w-0">
                  <div className="font-medium text-foreground/90 truncate">{u.name} {u.is_admin && <span className="text-overline text-secondary ml-1">admin</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
              </div>
              <div className="w-24 text-right text-sm font-medium">{Math.round(mt.calories || 0) || "—"}</div>
              <div className="w-20 text-right text-xs text-muted-foreground">{Math.round(mt.protein_g || 0)}/{Math.round(mt.carbs_g || 0)}/{Math.round(mt.fat_g || 0)}</div>
              <div className="w-24 text-right text-xs">
                {p.macros_admin_locked ? <span className="text-secondary">locked</span> : (p.onboarding_complete ? <span className="text-foreground/70">auto</span> : <span className="text-muted-foreground">incomplete</span>)}
                <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`user-plan-${u.user_id}`}>{planStatusLabel(p)}</div>
              </div>
              <div className="w-28 text-right flex gap-1 justify-end">
                <Button size="sm" variant="ghost" className="rounded-full px-2" onClick={() => setViewing(u)} data-testid={`view-user-${u.user_id}`}><Eye size={15} /></Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(u)} data-testid={`adjust-user-${u.user_id}`}>Adjust</Button>
              </div>
            </div>
          );
        })}
        {users.length === 0 && <div className="px-5 py-6 text-muted-foreground text-sm">No users yet.</div>}
      </div>
      <UserMacrosDialog user={selected} onClose={() => { setSelected(null); load(); }} />
      <UserDetailDialog user={viewing} onClose={() => setViewing(null)} onAdjust={(u) => { setViewing(null); setSelected(u); }} />
    </div>
  );
}

function UserMacrosDialog({ user, onClose }) {
  const [m, setM] = useState({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const mt = user.profile?.macro_targets || { calories: 2000, protein_g: 130, carbs_g: 220, fat_g: 65 };
    setM({
      calories: Math.round(mt.calories || 0),
      protein_g: Math.round(mt.protein_g || 0),
      carbs_g: Math.round(mt.carbs_g || 0),
      fat_g: Math.round(mt.fat_g || 0),
    });
    setLocked(!!user.profile?.macros_admin_locked);
  }, [user]);

  if (!user) return null;
  const p = user.profile || {};

  const grant = async (plan) => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.user_id}/grant`, { plan });
      toast.success(plan === "free" ? "Access revoked" : `Granted ${plan} plan`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.user_id}`, {
        macro_targets: m,
        macros_admin_locked: locked,
      });
      toast.success("Targets saved");
      onClose?.();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Adjust macros — {user.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2 mb-2">
          {p.age ? `${p.age}y` : "?"} · {p.gender || "—"} · {p.height_cm || "?"}cm · {p.weight_kg || "?"}kg → {p.target_weight_kg || "?"}kg · {p.goal || "—"} · {p.activity_level || "—"} · {p.dietary_preference || "—"}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Calories</Label><Input type="number" value={m.calories} onChange={(e) => setM({ ...m, calories: Number(e.target.value) })} data-testid="adm-cal" /></div>
          <div><Label>Protein (g)</Label><Input type="number" value={m.protein_g} onChange={(e) => setM({ ...m, protein_g: Number(e.target.value) })} data-testid="adm-protein" /></div>
          <div><Label>Carbs (g)</Label><Input type="number" value={m.carbs_g} onChange={(e) => setM({ ...m, carbs_g: Number(e.target.value) })} data-testid="adm-carbs" /></div>
          <div><Label>Fat (g)</Label><Input type="number" value={m.fat_g} onChange={(e) => setM({ ...m, fat_g: Number(e.target.value) })} data-testid="adm-fat" /></div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Switch checked={locked} onCheckedChange={setLocked} data-testid="adm-lock" />
          <span className="text-sm">Lock — prevents user auto-recalc from overriding these targets</span>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Label>Access plan</Label>
          <div className="text-xs text-muted-foreground mb-2" data-testid="adm-current-plan">
            Current: <strong>{planStatusLabel(p)}</strong> — granting a paid plan extends remaining time (stacks).
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "trial", label: "7 Days (trial)" },
              { k: "monthly", label: "1 Month" },
              { k: "yearly", label: "1 Year" },
              { k: "lifetime", label: "Lifetime" },
            ].map((pl) => (
              <Button
                key={pl.k}
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => grant(pl.k)}
                className="rounded-full"
                data-testid={`grant-${pl.k}`}
              >
                {pl.label}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => grant("free")}
              className="rounded-full text-destructive hover:text-destructive"
              data-testid="grant-free"
            >
              Revoke
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full bg-primary" data-testid="save-adm-macros">{saving ? "Saving…" : "Save targets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function fmtDate(s) {
  try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}
function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 240 ? s.slice(0, 240) + "…" : s;
  }
  return String(v);
}

function ProfileGrid({ p, hm, micros }) {
  const row = (label, val) => (
    <div className="flex justify-between gap-3 py-1.5 border-b border-border/60">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground/90 text-right">{fmtVal(val)}</span>
    </div>
  );
  const mt = p.macro_targets || {};
  const ca = p.clinical_assessment || {};
  const customMicros = Object.entries(micros || {}).filter(([, m]) => m.custom);
  return (
    <div className="grid sm:grid-cols-2 gap-x-6">
      <div>
        {row("Age", p.age)}
        {row("Gender", p.gender)}
        {row("Height", p.height_cm && `${p.height_cm} cm`)}
        {row("Weight", p.weight_kg && `${p.weight_kg} kg`)}
        {row("Target weight", p.target_weight_kg && `${p.target_weight_kg} kg`)}
        {row("Goal", p.goal)}
        {row("Activity", p.activity_level)}
        {row("Diet", p.dietary_preference)}
        {row("Location", [p.city, p.region, p.country].filter(Boolean).join(", "))}
        {hm?.bmi ? row("BMI", hm.bmi) : null}
        {row("Daily targets", `${Math.round(mt.calories || 0)} kcal · P${Math.round(mt.protein_g || 0)}/C${Math.round(mt.carbs_g || 0)}/F${Math.round(mt.fat_g || 0)}/Fib${Math.round(mt.fiber_g || 0)}`)}
      </div>
      <div>
        {row("Allergies", p.allergies)}
        {row("Medical conditions", p.medical_conditions)}
        {row("Medications", p.medications)}
        {row("Cuisines", p.cuisines)}
        {row("Preferred", p.preferred_ingredients)}
        {row("Disliked", p.disliked_foods)}
        {row("Food prefs", p.food_preferences)}
        {row("Grocery sources", p.grocery_sources)}
        {row("Notes", p.notes)}
      </div>
      <div className="sm:col-span-2 mt-3">
        <div className="text-overline mb-1">clinical assessment</div>
        {(ca.avoid || ca.recommended) ? (
          <div className="text-xs space-y-1">
            <div><span className="text-destructive font-medium">Avoid:</span> {fmtVal(ca.avoid)}</div>
            <div><span className="text-green-700 font-medium">Recommended:</span> {fmtVal(ca.recommended)}</div>
          </div>
        ) : <div className="text-xs text-muted-foreground">Not computed yet.</div>}
      </div>
      {customMicros.length > 0 && (
        <div className="sm:col-span-2 mt-3">
          <div className="text-overline mb-1">personalized micro targets</div>
          <div className="text-xs">{customMicros.map(([, m]) => `${m.label} ${m.rda}${m.unit}`).join(" · ")}</div>
        </div>
      )}
    </div>
  );
}

function UserDetailDialog({ user, onClose, onAdjust }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [mcOpen, setMcOpen] = useState(false);
  const [mcMeal, setMcMeal] = useState("lunch");
  const [mcDish, setMcDish] = useState("");
  const [mcLoading, setMcLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [savedRecipe, setSavedRecipe] = useState(false);
  const [dislikingMc, setDislikingMc] = useState(false);

  useEffect(() => {
    if (!user) { setData(null); setHistory([]); setTab("profile"); return; }
    (async () => {
      setLoading(true);
      try {
        const [o, h] = await Promise.all([
          api.get(`/admin/users/${user.user_id}/overview`),
          api.get(`/admin/users/${user.user_id}/profile-history`),
        ]);
        setData(o.data); setHistory(h.data || []);
      } catch (e) { toast.error("Failed to load user"); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const genMenuCard = async () => {
    setMcLoading(true); setSavedRecipe(false);
    try {
      const { data: r } = await api.post(`/admin/users/${user.user_id}/menu-card`, { meal: mcMeal, dish_name: mcDish });
      setRecipe(r); setRecipeOpen(true); setMcOpen(false);
    } catch (e) { toast.error("Could not generate menu card"); }
    finally { setMcLoading(false); }
  };

  const saveRecipe = async () => {
    if (!recipe || savedRecipe) return;
    try {
      await api.post(`/admin/users/${user.user_id}/save-recipe`, { recipe });
      setSavedRecipe(true);
      toast.success(`Saved to ${data?.user?.name || "user"}'s recipes`);
    } catch (e) { toast.error("Save failed"); }
  };

  const dislikeMenuCard = async (r) => {
    const cur = r || recipe;
    if (!cur || dislikingMc) return;
    setDislikingMc(true); setSavedRecipe(false);
    try {
      const { data: nr } = await api.post(`/admin/users/${user.user_id}/menu-card`, { meal: cur.meal || mcMeal, avoid: [cur.dish] });
      setRecipe(nr);
      toast.success(`Generated a different option: ${nr.dish}`);
    } catch (e) { toast.error("Couldn't generate another — retry"); }
    finally { setDislikingMc(false); }
  };

  if (!user) return null;
  const p = data?.profile || {};
  const c = data?.counts || {};

  return (
    <>
      <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="rounded-3xl max-w-3xl w-[calc(100vw-1rem)] sm:w-full h-[92dvh] sm:h-auto max-h-[92dvh] sm:max-h-[88vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl flex items-center gap-3">
              {data?.user?.picture && <img src={data.user.picture} alt="" className="w-10 h-10 rounded-full border border-border" />}
              <div>
                <div>{data?.user?.name || user.name}</div>
                <div className="text-xs text-muted-foreground font-sans">{data?.user?.email}</div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loading && <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && data && (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                <Button size="sm" className="rounded-full bg-primary" onClick={() => setMcOpen(true)} data-testid="create-menu-card-btn"><ChefHat size={14} className="mr-1" /> Create menu card</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAdjust?.(user)} data-testid="detail-adjust-btn">Adjust macros & plan</Button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3 text-center">
                {[["chats", "Queries"], ["food_logs", "Meals"], ["workouts", "Workouts"], ["recipes", "Recipes"], ["plans", "Plans"], ["profile_changes", "Edits"]].map(([k, lbl]) => (
                  <div key={k} className="bg-surface rounded-2xl border border-border py-2" data-testid={`count-${k}`}>
                    <div className="font-serif-display text-xl text-primary">{c[k] ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lbl}</div>
                  </div>
                ))}
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex-wrap h-auto">
                  <TabsTrigger value="profile" data-testid="udt-profile"><UsersIcon size={13} className="mr-1" />Profile</TabsTrigger>
                  <TabsTrigger value="enrichment" data-testid="udt-enrichment"><History size={13} className="mr-1" />Enrichment</TabsTrigger>
                  <TabsTrigger value="recipes" data-testid="udt-recipes"><Utensils size={13} className="mr-1" />Recipes</TabsTrigger>
                  <TabsTrigger value="queries" data-testid="udt-queries"><MessageSquare size={13} className="mr-1" />Queries</TabsTrigger>
                  <TabsTrigger value="logs" data-testid="udt-logs"><ClipboardList size={13} className="mr-1" />Logs</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="udt-activity"><Activity size={13} className="mr-1" />Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="mt-4 text-sm" data-testid="udt-profile-content">
                  <ProfileGrid p={p} hm={data.health_metrics} micros={data.micro_fields} />
                </TabsContent>

                <TabsContent value="enrichment" className="mt-4" data-testid="udt-enrichment-content">
                  {history.length === 0 && <div className="text-muted-foreground text-sm py-6 text-center">No profile changes recorded yet. Changes appear here as the user is enriched (onboarding, chat, admin, assessments).</div>}
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div key={h.id} className="bg-surface rounded-xl border border-border p-3 text-sm" data-testid="enrich-row">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{h.field}</span>
                          <span className="text-[10px] uppercase tracking-wide text-secondary bg-secondary/10 rounded-full px-2 py-0.5">{h.source}</span>
                        </div>
                        <div className="text-xs mt-1">
                          <span className="text-muted-foreground line-through">{fmtVal(h.old)}</span>
                          <span className="mx-2 text-foreground/40">→</span>
                          <span className="text-foreground/90">{fmtVal(h.new)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(h.ts)}</div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="recipes" className="mt-4" data-testid="udt-recipes-content">
                  {(!data.recipes || data.recipes.length === 0) && <div className="text-muted-foreground text-sm py-6 text-center">No recipes created/liked yet.</div>}
                  <div className="space-y-2">
                    {data.recipes?.map((r) => (
                      <button key={r.id} onClick={() => { setRecipe(r.recipe || r); setSavedRecipe(true); setRecipeOpen(true); }} className="w-full text-left bg-surface rounded-xl border border-border p-3 hover:border-primary/40" data-testid="recipe-row">
                        <div className="font-serif-display text-lg text-primary">{r.dish}</div>
                        <div className="text-xs text-muted-foreground capitalize">{r.meal}{r.created_by_admin ? " • added by admin" : ""} • {fmtDate(r.created_at)}</div>
                      </button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="queries" className="mt-4" data-testid="udt-queries-content">
                  {(!data.recent_queries || data.recent_queries.length === 0) && <div className="text-muted-foreground text-sm py-6 text-center">No chat queries yet.</div>}
                  <div className="space-y-2">
                    {data.recent_queries?.map((q, i) => (
                      <div key={i} className="bg-surface rounded-xl border border-border p-3 text-sm" data-testid="query-row">
                        <div className="text-foreground/90">{q.content}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(q.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="logs" className="mt-4 space-y-4" data-testid="udt-logs-content">
                  <div>
                    <div className="text-overline mb-2">recent meals</div>
                    {(data.logs?.food || []).slice(0, 12).map((f) => (
                      <div key={f.id} className="flex justify-between text-sm border-b border-border py-1.5">
                        <span className="truncate"><span className="text-muted-foreground capitalize text-xs mr-2">{f.meal}</span>{f.name}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{Math.round(f.calories)} kcal · {f.logged_for}</span>
                      </div>
                    ))}
                    {(!data.logs?.food || data.logs.food.length === 0) && <div className="text-muted-foreground text-sm">No meals logged.</div>}
                  </div>
                  <div>
                    <div className="text-overline mb-2">recent workouts</div>
                    {(data.logs?.workouts || []).slice(0, 8).map((w) => (
                      <div key={w.id} className="flex justify-between text-sm border-b border-border py-1.5">
                        <span className="truncate">{w.name} <span className="text-muted-foreground text-xs capitalize">{(w.types || []).join(", ")}</span></span>
                        <span className="text-muted-foreground whitespace-nowrap">{Math.round(w.calories_burned)} kcal · {w.logged_for}</span>
                      </div>
                    ))}
                    {(!data.logs?.workouts || data.logs.workouts.length === 0) && <div className="text-muted-foreground text-sm">No workouts logged.</div>}
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="mt-4" data-testid="udt-activity-content">
                  <div className="space-y-1">
                    {data.activity?.map((e, i) => (
                      <div key={i} className="flex justify-between text-sm border-b border-border py-1.5" data-testid="activity-row">
                        <span className="capitalize">{(e.type || "").replace(/_/g, " ")} {e.meta?.name ? `— ${e.meta.name}` : (e.meta?.dish ? `— ${e.meta.dish}` : "")}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{fmtDate(e.ts)}</span>
                      </div>
                    ))}
                    {(!data.activity || data.activity.length === 0) && <div className="text-muted-foreground text-sm py-6 text-center">No activity yet.</div>}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={mcOpen} onOpenChange={setMcOpen}>
        <DialogContent className="rounded-3xl max-w-md z-[65]" data-testid="menu-card-form">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Create menu card</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Personalized to {data?.user?.name || "this user"}'s profile (allergies, conditions, diet, locale).</p>
          <div className="space-y-3">
            <div>
              <Label>Meal</Label>
              <Select value={mcMeal} onValueChange={setMcMeal}>
                <SelectTrigger data-testid="mc-meal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="snack">Snack</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dish (optional — leave blank to let AI choose)</Label>
              <Input value={mcDish} onChange={(e) => setMcDish(e.target.value)} placeholder="e.g., High-protein paneer bowl" data-testid="mc-dish" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={genMenuCard} disabled={mcLoading} className="rounded-full bg-primary" data-testid="mc-generate-btn"><Sparkles size={15} className="mr-1" />{mcLoading ? "Generating…" : "Generate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecipeCardDialog
        open={recipeOpen}
        recipe={recipe}
        loading={false}
        liked={savedRecipe}
        onToggleLike={saveRecipe}
        onDislike={dislikeMenuCard}
        disliking={dislikingMc}
        onClose={() => setRecipeOpen(false)}
      />
    </>
  );
}


function SupportTab() {
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState("all"); // all | support | feedback | open
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "support" || filter === "feedback") params.type = filter;
      if (filter === "open") params.status = "open";
      const { data } = await api.get("/admin/support", { params });
      setItems(data.items || []);
      setOpenCount(data.open_count || 0);
    } catch (e) { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/support/${id}`, { status });
    toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    load();
  };

  const FILTERS = [
    { key: "all", label: "All" },
    { key: "open", label: `Open${openCount ? ` (${openCount})` : ""}` },
    { key: "support", label: "Support" },
    { key: "feedback", label: "Feedback" },
  ];

  return (
    <div data-testid="support-tab">
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`support-filter-${f.key}`}
            className={`text-sm px-4 py-1.5 rounded-full border transition ${filter === f.key ? "border-secondary bg-secondary/10 text-primary" : "border-border text-foreground/70 hover:bg-muted"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-8 text-center" data-testid="support-empty">No messages yet.</div>
      )}

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="bg-surface border border-border rounded-2xl p-4" data-testid={`support-item-${it.id}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${it.type === "feedback" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                  {it.type === "feedback" ? "Feedback" : "Support"}
                </span>
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${it.status === "resolved" ? "bg-green-50 text-green-700" : "bg-muted text-foreground/70"}`}>
                  {it.status === "resolved" ? "Resolved" : "Open"}
                </span>
                <span className="text-sm text-foreground/80">{it.name || it.email}</span>
                {it.email && <span className="text-xs text-muted-foreground">{it.email}</span>}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
              </span>
            </div>
            <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{it.message}</div>
            <div className="mt-3 flex justify-end">
              {it.status === "open" ? (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setStatus(it.id, "resolved")} data-testid={`support-resolve-${it.id}`}>
                  Mark resolved
                </Button>
              ) : (
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setStatus(it.id, "open")} data-testid={`support-reopen-${it.id}`}>
                  Reopen
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsTab() {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState("submitted"); // submitted | approved | rejected | all
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/admin/payments", { params });
      setItems(data.items || []);
      setPendingCount(data.pending_count || 0);
    } catch (e) { toast.error("Failed to load payments"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/payments/${id}/approve`); toast.success("Approved — plan activated"); load(); }
    catch (e) { toast.error("Could not approve"); }
    finally { setBusy(""); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejecting (optional):") ?? "";
    setBusy(id);
    try { await api.post(`/admin/payments/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error("Could not reject"); }
    finally { setBusy(""); }
  };

  const FILTERS = [
    { key: "submitted", label: `To review${pendingCount ? ` (${pendingCount})` : ""}` },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
  ];
  const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;
  const STATUS_STYLE = {
    submitted: "bg-amber-50 text-amber-700", approved: "bg-green-50 text-green-700",
    rejected: "bg-red-50 text-red-700", initiated: "bg-muted text-foreground/60",
  };

  return (
    <div data-testid="payments-tab">
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`payments-filter-${f.key}`}
            className={`text-sm px-4 py-1.5 rounded-full border transition ${filter === f.key ? "border-secondary bg-secondary/10 text-primary" : "border-border text-foreground/70 hover:bg-muted"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-8 text-center" data-testid="payments-empty">No payments here.</div>
      )}

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="bg-surface border border-border rounded-2xl p-4" data-testid={`payment-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-primary">{it.plan_name}</span>
                  <span className="text-sm text-foreground/80">{rupees(it.amount_paise)}</span>
                  <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_STYLE[it.status] || "bg-muted"}`}>{it.status}</span>
                </div>
                <div className="text-sm text-foreground/80 mt-1">{it.name || it.email} <span className="text-muted-foreground">· {it.email}</span></div>
                <div className="text-xs text-muted-foreground mt-1">
                  Ref <span className="font-mono">{it.reference}</span>
                  {it.utr && <> · UTR <span className="font-mono text-foreground/80">{it.utr}</span></>}
                  {it.created_at && <> · {new Date(it.created_at).toLocaleString()}</>}
                </div>
                {it.note && <div className="text-xs text-foreground/70 mt-1 italic">"{it.note}"</div>}
                {it.reject_reason && <div className="text-xs text-red-600 mt-1">Rejected: {it.reject_reason}</div>}
              </div>
              {it.status === "submitted" && (
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full bg-primary" disabled={busy === it.id} onClick={() => approve(it.id)} data-testid={`payment-approve-${it.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={busy === it.id} onClick={() => reject(it.id)} data-testid={`payment-reject-${it.id}`}>Reject</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

